/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import UIConstants from "../../UIConstants";
import { useAuthenticationContext } from "../Authentication/AuthenticationContext";
import IAuthenticationContext from "../Authentication/AuthenticationContext/IAuthenticationContext";
import { useAppSnackbar } from "../Components/AppSnackbarContext";
import { Code } from "../Components/AppSnackbarContext/AppSnackbarItem";
import IAppSnackbar from "../Components/AppSnackbarContext/IAppSnackbar";
import { environment } from "../environment";
import CloudApolloClient from "../GraphQL/CloudApolloClient";
import CloudApolloErrorHandler from "../GraphQL/CloudApolloErrorHandler";
import AnyUtil from "./AnyUtil";
import MandantUtil from "./MandantUtil";
import NetworkErrorUtil from "./NetworkErrorUtil";
import StringUtil from "./StringUtil";
import useZeigeAuthFehler, { ZeigeAuthFehlerFunc } from "./useZeigeAuthFehler";

const ACTION_RETRY = "Retry";

const inMemoryCache = () => new InMemoryCache({ addTypename: false });

const errorLink = (snackbar: IAppSnackbar) => {
    return onError((e) => {
        // https://www.apollographql.com/docs/react/data/error-handling/#on-graphql-errors
        if (e.graphQLErrors) {
            for (const err of e.graphQLErrors) {
                if (err.extensions.action === ACTION_RETRY) {
                    return e.forward(e.operation);
                }
            }
        }

        const cloudApolloErrorHandler = new CloudApolloErrorHandler();
        if (cloudApolloErrorHandler.handle(e, snackbar) && e.response) {
            e.response.errors = undefined;
        }
    });
}

const unauthorizedLink = (snackbar: IAppSnackbar, zeigeAuthFehler: ZeigeAuthFehlerFunc, authContext?: IAuthenticationContext) => {
    return onError((e) => {
        const errorCodes = e.graphQLErrors && Array.isArray(e.graphQLErrors) ? e.graphQLErrors?.map(error => error.extensions?.code) : [];
        if (NetworkErrorUtil.isUnauthorized(e.networkError) || errorCodes?.includes("AUTH_NOT_AUTHENTICATED")) {
            authContext?.setIsLoggedOut();

            const serverErrorWithResult: any = e.networkError as any;
            snackbar.pushCustomError("CloudApolloClientProvider", serverErrorWithResult?.result ?? "");

            // Diese Rückgabe bricht die Ausführung ab
            return new Observable((subscriber) => {
                subscriber.complete();
            });
        }

        const notAuthorized: Code = "AUTH_NOT_AUTHORIZED";
        if (errorCodes?.includes(notAuthorized)) {
            zeigeAuthFehler(true, notAuthorized);

            // Diese Rückgabe bricht die Ausführung ab
            return new Observable((subscriber) => {
                subscriber.complete();
            });
        }
    });
}

const httpLink = (snackbar: IAppSnackbar, zeigeAuthFehler: ZeigeAuthFehlerFunc, url: string, authContext?: IAuthenticationContext) => {
    // Hierdurch wird verhindert, dass fehlerhafte
    // Requests gefeuert werden, weil noch kein Mandant
    // zur Verfügung steht.
    const mandant = MandantUtil.ermittleMandant(new URL(url).pathname);
    if (StringUtil.isEmpty(mandant) || mandant === UIConstants.uiSysPath) {
        return undefined;
    }

    return ApolloLink.from([
        errorLink(snackbar),
        unauthorizedLink(snackbar, zeigeAuthFehler, authContext),
        createHttpLink({
            uri: url,
            credentials: UIConstants.credentials, // Cookies bei selber Domäne mitsenden
        }),
    ]);
}

const cloudLink = (snackbar: IAppSnackbar, zeigeAuthFehler: ZeigeAuthFehlerFunc, url: string, authContext?: IAuthenticationContext) => {
    const httpLinkInstance = httpLink(snackbar, zeigeAuthFehler, url, authContext);
    if (AnyUtil.isNullOrUndefined(httpLinkInstance)) {
        return undefined;
    }

    return ApolloLink.from([httpLinkInstance]);
}

export default function useCloudApolloClient(url?: string): CloudApolloClient {
    const snackbar = useAppSnackbar();
    const authContext = useAuthenticationContext();
    const zeigeAuthFehler = useZeigeAuthFehler();
    url = url ?? environment.httpsBaseUrl;

    const cloudLinkInstance = cloudLink(snackbar, zeigeAuthFehler, url, authContext ?? undefined);
    const apolloClient = new ApolloClient({
        link: cloudLinkInstance,
        cache: inMemoryCache(),
        resolvers: {},
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'no-cache',
            },
            query: {
                fetchPolicy: 'no-cache'
            }
        }
    });

    return new CloudApolloClient(apolloClient, url);
}
